// View-models that turn the real engine docs (Entry.duelPicks + duel Fixtures) into
// the shapes the cycling round screens render. Scoring goes through the shared pure
// engine (scoreCyclingEntry) so the numbers shown match exactly what the server
// settled — the cycling analogue of lib/round.ts for football.
import {
  scoreCyclingEntry,
  type DuelSide,
  type Entry,
  type Fixture,
  type RiderDuelSettlement
} from '@h2h/shared';

export type DuelStatus = 'upcoming' | 'settled' | 'void';

export interface DuelVM {
  duelId: string;
  speciality?: string | null;
  riderAName: string;
  riderBName: string;
  /** Trade team + nationality per rider, for the name + team subtitle (matches the pick screen). */
  riderATeam?: string | null;
  riderBTeam?: string | null;
  riderANat?: string | null;
  riderBNat?: string | null;
  /** The side the player backed (A/B), or null if somehow unset. */
  pick: DuelSide | null;
  /** Rider the player backed, and the one they faded — for the recap copy. */
  pickedName: string;
  otherName: string;
  confidence: number;
  status: DuelStatus;
  /** Winning side once settled (null on a void/push or pre-settle). */
  winner: DuelSide | null;
  /** True/false once decided; null while upcoming or on a void. */
  correct: boolean | null;
  /** Confidence-weighted points: null until the duel resolves, 0 on a miss/void. */
  points: number | null;
  kickoffAt: number;
}

/** Best display name for a rider ref, falling back to the mirrored team field then url. */
function riderName(ref: Fixture['riderA'], fallback?: string): string {
  return ref?.riderName ?? fallback ?? ref?.riderUrl ?? '—';
}

/**
 * Reconstruct the settlement map the shared scorer expects from the verdicts stamped
 * on the duel fixtures (`duelResult`). scoreCyclingEntry only reads winner + outcome,
 * so the participant rows are filled minimally from the fixture's rider refs.
 */
function settlementsFrom(fixtures: Map<string, Fixture>): Map<string, RiderDuelSettlement> {
  const m = new Map<string, RiderDuelSettlement>();
  for (const f of fixtures.values()) {
    const dr = f.duelResult;
    if (!dr) continue;
    m.set(f.fixtureId, {
      winner: dr.winner,
      outcome: dr.outcome,
      reason: '',
      a: { riderUrl: f.riderA?.riderUrl ?? '', riderName: f.riderA?.riderName ?? null, rank: null, status: null, found: true },
      b: { riderUrl: f.riderB?.riderUrl ?? '', riderName: f.riderB?.riderName ?? null, rank: null, status: null, found: true }
    });
  }
  return m;
}

/** Per-duel view-models for an entry, banker first (highest confidence on top). */
export function buildDuelVMs(entry: Entry, fixtures: Map<string, Fixture>): DuelVM[] {
  const settlements = settlementsFrom(fixtures);
  const score = scoreCyclingEntry(
    { userId: entry.userId, picks: entry.duelPicks ?? [], submittedAt: entry.submittedAt },
    settlements
  );
  const picks = [...(entry.duelPicks ?? [])].sort((a, b) => b.confidenceWeight - a.confidenceWeight);
  return picks.map((p) => {
    const f = fixtures.get(p.duelId);
    const aName = riderName(f?.riderA, f?.homeTeam);
    const bName = riderName(f?.riderB, f?.awayTeam);
    const dr = f?.duelResult ?? null;
    const status: DuelStatus =
      f?.status === 'VOID' || dr?.outcome === 'void' ? 'void' : f?.status === 'SETTLED' && dr ? 'settled' : 'upcoming';
    const winner = dr?.winner ?? null;
    const correct = status === 'settled' ? winner === p.pick : null;
    const points = status === 'upcoming' ? null : score.scoreByDuel[p.duelId] ?? 0;
    return {
      duelId: p.duelId,
      speciality: f?.speciality ?? null,
      riderAName: aName,
      riderBName: bName,
      riderATeam: f?.riderA?.teamName ?? null,
      riderBTeam: f?.riderB?.teamName ?? null,
      riderANat: f?.riderA?.nationality ?? null,
      riderBNat: f?.riderB?.nationality ?? null,
      pick: p.pick,
      pickedName: p.pick === 'A' ? aName : bName,
      otherName: p.pick === 'A' ? bName : aName,
      confidence: p.confidenceWeight,
      status,
      winner,
      correct,
      points,
      kickoffAt: f?.kickoffAt ?? 0
    };
  });
}

/** The entry's stage score so far (sum of resolved duel points). */
export function stageScore(entry: Entry, fixtures: Map<string, Fixture>): number {
  return buildDuelVMs(entry, fixtures).reduce((sum, d) => sum + (d.points ?? 0), 0);
}

/** How many of this stage's duels have reached a terminal (settled/void) state. */
export function duelsResolved(fixtures: Map<string, Fixture>): number {
  let n = 0;
  for (const f of fixtures.values()) if (f.status === 'SETTLED' || f.status === 'VOID') n++;
  return n;
}

/** A compact "ARS short rider v rider" matchup label (last name where possible). */
export function shortRider(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : name;
}
